import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  Button
} from '@mui/material';
import {
  CloudUpload,
  Description,
  CheckCircle,
  Error,
  Info,
  ArrowBack
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

interface UploadResult {
  success: boolean;
  raceId?: string;
  message?: string;
  routeData?: {
    totalDistance: number;
    totalPoints: number;
    elevationProfile: {
      minElevation: number;
      maxElevation: number;
      elevationGain: number;
      elevationLoss: number;
    };
    waypoints: number;
    segments: number;
  };
}

const RouteUpload: React.FC = () => {
  const [raceId, setRaceId] = useState('');
  const [raceName, setRaceName] = useState('');
  const [stageNumber, setStageNumber] = useState(1);
  const [stageName, setStageName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCuesheetFile, setSelectedCuesheetFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setSelectedFile(file);
    setUploadResult(null); // Clear any previous results
  }, []);

  const onCuesheetDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setSelectedCuesheetFile(file);
  }, []);

  const handleSubmit = async () => {
    // Validation
    if (!raceId.trim() || !raceName.trim() || !stageName.trim()) {
      setUploadResult({
        success: false,
        message: 'Please fill in all required fields'
      });
      return;
    }

    if (!selectedFile) {
      setUploadResult({
        success: false,
        message: 'Please select a route file to upload'
      });
      return;
    }

    // Handle upload
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('routeFile', selectedFile);
      if (selectedCuesheetFile) {
        formData.append('cuesheetFile', selectedCuesheetFile);
      }
      formData.append('raceId', raceId.trim());
      formData.append('raceName', raceName.trim());
      formData.append('stageNumber', stageNumber.toString());
      formData.append('stageName', stageName.trim());

      // Simulate progress for user feedback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/routes/upload`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const result = await response.json();

      if (response.ok) {
        setUploadResult({
          success: true,
          raceId: raceId,
          message: 'Route uploaded and processed successfully!',
          routeData: result.data
        });
        // Clear form
        setRaceId('');
        setRaceName('');
        setStageNumber(1);
        setStageName('');
        setSelectedFile(null);
        setSelectedCuesheetFile(null);
      } else {
        setUploadResult({
          success: false,
          message: result.error || 'Upload failed'
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: 'Network error during upload'
      });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.fit'],
      'application/gpx+xml': ['.gpx'],
      'text/xml': ['.gpx']
    },
    maxFiles: 1,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: () => setDragActive(false),
    onDropRejected: () => setDragActive(false)
  });

  const { getRootProps: getCuesheetRootProps, getInputProps: getCuesheetInputProps, isDragActive: isCuesheetDragActive } = useDropzone({
    onDrop: onCuesheetDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/csv': ['.csv']
    },
    maxFiles: 1
  });

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/routes')}
          sx={{ mb: 1 }}
        >
          Back to Routes
        </Button>
        <Typography variant="h4" gutterBottom>
          Route Upload
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload Race Route (.fit or .gpx)
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload .fit or .gpx files to process race routes. Files are processed and stored in the database
          with GPS coordinates, elevation profiles, waypoints, and climb segments.
        </Typography>

        {/* Race Information */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="Race Name"
            value={raceName}
            onChange={(e) => setRaceName(e.target.value)}
            placeholder="e.g., Tour de France 2024, Giro d'Italia 2024"
            disabled={uploading}
            helperText="Name of the race"
            required
          />
          <TextField
            type="number"
            label="Stage Number"
            value={stageNumber}
            onChange={(e) => setStageNumber(Number(e.target.value))}
            inputProps={{ min: 1, max: 50 }}
            sx={{ minWidth: 120 }}
            disabled={uploading}
            helperText="Stage number"
            required
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="Stage Name"
            value={stageName}
            onChange={(e) => setStageName(e.target.value)}
            placeholder="e.g., Paris - Roubaix, Mountain Stage"
            disabled={uploading}
            helperText="Descriptive name for this stage"
            required
          />
          <TextField
            fullWidth
            label="Race ID"
            value={raceId}
            onChange={(e) => setRaceId(e.target.value)}
            placeholder="e.g., TDF2024_Stage1, GIRO2024_Stage15"
            disabled={uploading}
            helperText="Unique identifier for this race route"
            required
          />
        </Box>

        {/* File Drop Zone */}
        <Box
          {...getRootProps()}
          sx={{
            border: 2,
            borderColor: dragActive || isDragActive ? 'primary.main' : 'grey.300',
            borderStyle: 'dashed',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            bgcolor: dragActive || isDragActive ? 'action.hover' : 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': !uploading ? {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            } : {}
          }}
        >
          <input {...getInputProps()} disabled={uploading} />

          <CloudUpload
            sx={{
              fontSize: 48,
              color: dragActive || isDragActive ? 'primary.main' : 'grey.400',
              mb: 2
            }}
          />

          {dragActive || isDragActive ? (
            <Typography variant="h6" color="primary">
              Drop the file here...
            </Typography>
          ) : selectedFile ? (
            <>
              <Typography variant="h6" gutterBottom color="success.main">
                File Selected: {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to select a different file
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Drag & drop a route file here
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                or click to select a file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported: .fit, .gpx files (max 50MB)
              </Typography>
            </>
          )}
        </Box>

        {/* Cuesheet Drop Zone */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Cuesheet File (Optional)
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a CSV cuesheet to replace waypoint names with turn-by-turn directions.
          The CSV should contain columns: Type, Notes, Distance (km) From Start, Elevation (m).
        </Typography>

        <Box
          {...getCuesheetRootProps()}
          sx={{
            border: 2,
            borderColor: isCuesheetDragActive ? 'primary.main' : 'grey.300',
            borderStyle: 'dashed',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            bgcolor: isCuesheetDragActive ? 'action.hover' : 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': !uploading ? {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            } : {}
          }}
        >
          <input {...getCuesheetInputProps()} disabled={uploading} />

          <Description
            sx={{
              fontSize: 36,
              color: isCuesheetDragActive ? 'primary.main' : 'grey.400',
              mb: 1
            }}
          />

          {isCuesheetDragActive ? (
            <Typography variant="body1" color="primary">
              Drop the cuesheet file here...
            </Typography>
          ) : selectedCuesheetFile ? (
            <>
              <Typography variant="body1" gutterBottom color="success.main">
                Cuesheet Selected: {selectedCuesheetFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Size: {(selectedCuesheetFile.size / 1024).toFixed(2)} KB
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to select a different file
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                Drag & drop a cuesheet CSV here (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                or click to select a file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported: .csv files only
              </Typography>
            </>
          )}
        </Box>

        {/* Submit Button */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={uploading || !selectedFile || !raceId.trim() || !raceName.trim() || !stageName.trim()}
            sx={{ minWidth: 200 }}
          >
            {uploading ? 'Uploading...' : 'Upload Route'}
          </Button>
          {selectedFile && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'success.main' }}>
              Ready to upload: {selectedFile.name}
              {selectedCuesheetFile && ` + ${selectedCuesheetFile.name}`}
            </Typography>
          )}
        </Box>

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              Processing route file...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {uploadProgress}% complete
            </Typography>
          </Box>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <Box sx={{ mt: 3 }}>
            <Alert
              severity={uploadResult.success ? 'success' : 'error'}
              icon={uploadResult.success ? <CheckCircle /> : <Error />}
            >
              {uploadResult.message}
            </Alert>

            {uploadResult.success && uploadResult.routeData && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="subtitle1" gutterBottom>
                  <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Route Processing Results
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip
                    label={`${uploadResult.routeData.totalDistance} km`}
                    size="small"
                    color="info"
                  />
                  <Chip
                    label={`${uploadResult.routeData.totalPoints?.toLocaleString() || 0} GPS points`}
                    size="small"
                    color="info"
                  />
                  <Chip
                    label={`${uploadResult.routeData.elevationProfile?.elevationGain || 0}m elevation gain`}
                    size="small"
                    color="info"
                  />
                  <Chip
                    label={`${uploadResult.routeData.elevationProfile?.elevationLoss || 0}m elevation loss`}
                    size="small"
                    color="info"
                  />
                  <Chip
                    label={`${uploadResult.routeData.waypoints} waypoints`}
                    size="small"
                    color="info"
                  />
                  <Chip
                    label={`${uploadResult.routeData.segments} climb segments`}
                    size="small"
                    color="info"
                  />
                </Stack>
              </Paper>
            )}
          </Box>
        )}
      </Paper>

      {/* Information Panel */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
          File Processing Information
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Supported File Types:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip icon={<Description />} label=".fit files" size="small" />
          <Chip icon={<Description />} label=".gpx files" size="small" />
        </Stack>

        <Typography variant="subtitle2" gutterBottom>
          Processing Features:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          • GPS coordinate extraction and validation<br/>
          • Elevation profile calculation (gain/loss/gradient)<br/>
          • Automatic waypoint detection (start, finish, feed zones)<br/>
          • Climb segment identification (&gt;3% grade for &gt;500m)<br/>
          • Route distance and timing calculations<br/>
          • Data stored in MongoDB for fast querying
        </Typography>
      </Paper>
    </Box>
  );
};

export default RouteUpload;